import React, { useEffect } from 'react';
import { RouterProvider } from 'react-router-dom';
import useAPI from './api';
import { ThemeProvider, CssBaseline } from '@mui/material';
import useRouter from './Router';
import { useAtomValue, useSetAtom } from 'jotai';
import {
  authCheckedAtom,
  emailDomainAtom,
  moduleFlagsAtom,
  routesAtom,
  themeAtom,
  userDataAtom,
  userPermissionsAtom,
} from './Atoms';
import useTheme from './Theme/useTheme';

const App = function App() {
  const API = useAPI();
  const themeMode = useAtomValue(themeAtom);
  const theme = useTheme(themeMode);
  const userData = useAtomValue(userDataAtom);
  const authChecked = useAtomValue(authCheckedAtom);
  const setRoutes = useSetAtom(routesAtom);
  const setPermissions = useSetAtom(userPermissionsAtom);
  const setModuleFlags = useSetAtom(moduleFlagsAtom);
  const setEmailDomain = useSetAtom(emailDomainAtom);

  useEffect(() => {
    if (!userData) return;

    API.getBootstrap(
      (result) => {
        setRoutes(result?.routes ?? []);
        setPermissions(result?.permissions ?? []);
        setModuleFlags(result?.flags ?? {});
        // Null where the registry holds none, and in a single installation. The form then keeps
        // whatever env.sh injected, which is what a single installation has always used.
        setEmailDomain(result?.emailDomain ?? null);
      },
      // Fail open on the shell, closed on the capabilities: empty is a real
      // answer, so the app still renders while gated controls fall back to their
      // defaults. Leaving these null would hold the whole shell back on a call
      // that used to be allowed to fail silently.
      () => {
        setPermissions([]);
        setModuleFlags({});
      }
    );
  }, [userData]);

  useEffect(() => {
    const _mtm = (window._mtm = window._mtm || []);
    _mtm.push({ 'mtm.startTime': new Date().getTime(), event: 'mtm.Start' });
  }, []);

  const { router, routerKey } = useRouter(userData, authChecked);
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      {/* `key` forces a remount on login/logout and role changes:
          RouterProvider otherwise keeps the router it first mounted with. */}
      <RouterProvider key={routerKey} router={router} />
    </ThemeProvider>
  );
};

export default App;
